/**
 * useInstanceOptions — the Region and Instance Type option lists for the Add
 * Instance form, with their loading / error state.
 *
 * Nothing is requested until its inputs exist: regions need a provider and a
 * signed-in user; instance types also need a chosen region (legacy skips the
 * call when the region is null, ManageAccount.vue:802).
 *
 * `currentData`, not `data`: `data` keeps the PREVIOUS arguments' result while
 * a new request is in flight, which would show AWS's regions under Azure, or the
 * last region's instance types under a new region, until the answer arrives.
 */
import { useSelector } from 'react-redux';

import { selectUser } from '@security/auth/authSlice';

import {
  useGetCloudInstancesQuery,
  useGetInstanceSizesQuery,
  useGetRegionsQuery,
} from '../api/instanceOptionsApi';

const EMPTY = Object.freeze([]);

/*
 * Ask the server EVERY time the provider / region changes, even back to one
 * already loaded — as legacy does (ManageAccount.vue:596-600). Without this RTK
 * Query would answer a repeat choice from its cache for ~60s with no request,
 * which reads as "the API is not called" in testing.
 */
const ALWAYS_REFETCH = { refetchOnMountOrArgChange: true };

/** Message for a failed list, from the normalised API error when there is one. */
function errorMessage(query, fallback) {
  if (!query.isError) return undefined;
  return query.error?.normalized?.message ?? fallback;
}

/**
 * @param {{ provider: string, region: string,
 *           regions?: boolean, instanceTypes?: boolean, allInstanceTypes?: boolean }} args
 *   `regions` / `instanceTypes` switch each list off (default on) — the
 *   instance table only needs a list while one of those cells is being edited.
 *   `allInstanceTypes` (default off) adds EVERY instance type of the provider,
 *   any region (/cloud-instances) — Find and Replace's "To" list.
 */
export function useInstanceOptions({
  provider,
  region,
  regions = true,
  instanceTypes = true,
  allInstanceTypes = false,
}) {
  const email = useSelector(selectUser)?.email ?? '';

  const regionsQuery = useGetRegionsQuery(
    { provider, email },
    { ...ALWAYS_REFETCH, skip: !regions || !provider || !email },
  );
  const sizesQuery = useGetInstanceSizesQuery(
    { provider, region, email },
    { ...ALWAYS_REFETCH, skip: !instanceTypes || !provider || !region || !email },
  );
  const allQuery = useGetCloudInstancesQuery(
    { provider, email },
    { ...ALWAYS_REFETCH, skip: !allInstanceTypes || !provider || !email },
  );

  return {
    allInstanceTypes: allQuery.currentData ?? EMPTY,
    allInstanceTypesLoading: allQuery.isFetching,
    allInstanceTypesError: errorMessage(allQuery, 'Could not load instance types.'),
    regions: regionsQuery.currentData ?? EMPTY,
    regionsLoading: regionsQuery.isFetching,
    regionsError: errorMessage(regionsQuery, 'Could not load regions.'),
    instanceTypes: (region && sizesQuery.currentData) || EMPTY,
    instanceTypesLoading: sizesQuery.isFetching,
    instanceTypesError: errorMessage(sizesQuery, 'Could not load instance types.'),
  };
}
